import deepmerge from "deepmerge"
import { captureException, setUser } from "@sentry/react"
import {
  PublicClientApplication,
  LogLevel,
  EventType,
  InteractionRequiredAuthError
} from "@azure/msal-browser"
import type {
  ILoggerCallback,
  RedirectRequest,
  EndSessionRequest,
  EventMessage,
  AuthenticationResult
} from "@azure/msal-browser"
import { logError, logWarning } from "@/v1-ui/utils/utils.log"
import history from "@/v1-console/router/router.history"
import { ONE_HOUR_IN_MS } from "@/v1-console/constants"
import config from "@/v1-console/config"
import { ROUTES as R } from "@/v1-console/router/router.constants"
import { getLoginId, setLoginId, clearLoginId }
  from "@/v1-console/currentUser/currentUser.login"
import { trackAuthLogin, trackAuthLogout }
  from "@/v1-console/auth/auth.tracking"

const {
  azAuthLoginDomain,
  azAuthTenantId,
  azAuthClientId,
  azAuthLoginPolicyName,
  azAuthRegisterPolicyName,
  azAuthRedirectUrl,
  azAuthPostLogoutRedirectUri,
  isProd,
  isDev,
  apiUrl
} = config

const loggerCallback: ILoggerCallback = (level, msg, _hasPii) => {
  // if(hasPii) return
  // if(level === LogLevel.Verbose) {
  //   console.debug(msg)
  // }
  // if(level === LogLevel.Info) {
  //   console.info(msg)
  // }
  if(level === LogLevel.Warning) {
    logWarning(msg)
  }
  if(level === LogLevel.Error) {
    logError(msg)
  }
}

export const instance = new PublicClientApplication({
  auth: {
    clientId: azAuthClientId,
    knownAuthorities: [ azAuthLoginDomain ],
    redirectUri: azAuthRedirectUrl,
    postLogoutRedirectUri: azAuthPostLogoutRedirectUri
  },
  cache: {
    cacheLocation: isDev
      ? "sessionStorage"
      : "localStorage"
  },
  system: {
    loggerOptions: {
      loggerCallback
    }
  }
})

export let idToken: string = null

const accounts = instance.getAllAccounts()
if(accounts.length > 0) {
  instance.setActiveAccount(accounts[0])
}

instance.addEventCallback((e: EventMessage) => {
  if(e.eventType === EventType.LOGIN_SUCCESS
    || e.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
  ) {
    const d = e.payload as AuthenticationResult
    idToken = d.idToken
    instance.setActiveAccount(d.account)
  }

  if(e.eventType === EventType.LOGIN_SUCCESS) {
    trackAuthLogin()
  }

  if(e.eventType === EventType.LOGOUT_START) {
    trackAuthLogout()
    // Best-effort revoke this device's single-active-session login server-side
    // before we drop the token, so a flagged user's row never lingers to fire
    // a false LOGIN_CONFLICT on their next sign-in. Reads idToken + LoginId,
    // so it must run before the two are cleared just below.
    revokeSessionOnLogout()
    clearLoginId()
    instance.setActiveAccount(null)
    idToken = null
    setUser(null)
  }

  if(e.error) {
    onAuthError(e)
  }
})

function onAuthErrorInteractionRequired(_e: EventMessage) {
  instance.acquireTokenRedirect({
    ...loginRequest,
    extraQueryParameters: {
      ui_locales: getLocale()
    },
    account: instance.getActiveAccount()
  })
}

function onAuthError(e: EventMessage) {
  if(e.error instanceof InteractionRequiredAuthError) {
    onAuthErrorInteractionRequired(e)
    return
  }

  if(!isProd) {
    logError(e.error)
    return
  }

  captureException(e.error, {
    contexts: {
      "Auth": {
        eventType: e.eventType,
        interactionType: e.interactionType,
        payload: e.payload,
        timestamp: e.timestamp
      }
    }
  })
}

const DEFAULT_LANG_CODE = "nb"

const DEFAULT_REQUEST = {
  scopes: [
    "openid",
    "offline_access",
    "email",
    "profile",
    azAuthClientId
  ],
  extraQueryParameters: {
    ui_locales: DEFAULT_LANG_CODE
  }
}

/**
 * This is a hardcoded list that represents all the language that have
 * been configured for the login template in the b2c tenants.
 *
 * It's important that we ensure the correct language code is sent in the
 * requests, because if an invalid code is passed, the login server will
 * return an error, and the user will be stuck.
 */
const LANG_WHITELIST = new Set([ "nb", "en", "bn" ])

function getLocale() {
  let code = DEFAULT_LANG_CODE
  if(navigator.language) {
    const bCode = navigator.language.split("-")[0]
    if(LANG_WHITELIST.has(bCode)) {
      code = bCode
    }
  }
  return code
}

function getAuthority(policy: string) {
  return `https://${azAuthLoginDomain}/${azAuthTenantId}/${policy}`
}

function getAccountUserName() {
  const account = instance.getActiveAccount()
  return account?.username
}

const loginRequest: RedirectRequest = {
  ...DEFAULT_REQUEST,
  authority: getAuthority(azAuthLoginPolicyName)
}

const registerRequest: RedirectRequest = {
  ...DEFAULT_REQUEST,
  authority: getAuthority(azAuthRegisterPolicyName)
}

const logoutRequest: EndSessionRequest = {
  ...DEFAULT_REQUEST,
  authority: getAuthority(azAuthLoginPolicyName)
}

export function register(request: Partial<RedirectRequest> = {}) {
  return instance.loginRedirect(deepmerge({
    ...registerRequest,
    extraQueryParameters: {
      ui_locales: getLocale()
    }
  }, request))
}

// No forced prompt:"login". It forced a full credential + SMS re-auth on every
// call, including recovery, so any /session hiccup or single-active-session
// displacement burned a fresh SMS and could trip B2C's per-user SMS cap (issue
// #1080, FUS-809). The default prompt lets B2C reissue silently while its SSO
// session is valid. Callers needing a fresh prompt pass `{ prompt: "login" }`.
export function login(request: Partial<RedirectRequest> = {}) {
  const c = deepmerge({
    ...loginRequest,
    loginHint: getAccountUserName(),
    extraQueryParameters: {
      ui_locales: getLocale()
    }
  }, request)
  return instance.loginRedirect(c)
}

export function logout() {
  instance.logoutRedirect({
    ...logoutRequest,
    logoutHint: getAccountUserName(),
    idTokenHint: idToken,
    extraQueryParameters: {
      ui_locales: getLocale()
    }
  })
}

export function acquireTokenSilent() {
  return instance.acquireTokenSilent({
    ...loginRequest,
    redirectUri: "/static/blank.html",
    account: instance.getActiveAccount(),
    extraQueryParameters: {
      ui_locales: getLocale()
    }
  })
}

const t = setInterval(async () => {
  try {
    await acquireTokenSilent()
  } catch(error) {
    logError(error)
    clearInterval(t)
  }
}, ONE_HOUR_IN_MS)

// The other live device shown in the login-conflict screen. Shape matches
// the backend liveLogin DTO (v1-console-api session.handler.ts).
export type LiveLoginInfo = {
  deviceLabel: string | null,
  ipAddress: string | null,
  ipCity: string | null,
  ipCountry: string | null,
  lastSeenAt: string
}

// Thrown when /auth-check reports another live login for a flagged user
// (409). Carries the other device's details for the conflict screen.
class LoginConflictError extends Error {
  liveLogin?: LiveLoginInfo
  constructor(liveLogin?: LiveLoginInfo) {
    super("LOGIN_CONFLICT")
    this.name = "LoginConflictError"
    this.liveLogin = liveLogin
  }
}

// Thrown when this device's login was revoked elsewhere (401). The user
// continued on another device, so this one is signed out.
class SignedOutElsewhereError extends Error {
  constructor() {
    super("SIGNED_OUT_ELSEWHERE")
    this.name = "SignedOutElsewhereError"
  }
}

export async function checkTokenIsInSyncWithApi() {
  const headers: Record<string, string> = {
    authorization: `Bearer ${idToken}`
  }
  // Single-active-session: echo this device's login back to the backend.
  // Absent for unflagged users and before the first mint.
  const loginId = getLoginId()
  if(loginId) headers["x-login-id"] = loginId

  const res = await fetch(apiUrl + "/auth-check", { headers })

  // Original behaviour: 204 means signed in, nothing more to do. Reached
  // by everyone when enforcement is off and by unflagged users always.
  if(res.status === 204) return true

  // Below here only fires for flagged users with enforcement on.
  // 200: the backend minted this device's login. Persist and continue.
  if(res.status === 200) {
    const body = await res.json().catch(() => null)
    if(body?.loginId) setLoginId(body.loginId)
    return true
  }

  // 409: another device holds the single live login. Hand the caller the
  // other device's details so it can render the choice screen.
  if(res.status === 409) {
    const body = await res.json().catch(() => null)
    throw new LoginConflictError(body?.liveLogin)
  }

  if(res.status === 401) {
    const body = await res.json().catch(() => null)
    if(body?.code === "SIGNED_OUT_ELSEWHERE") {
      throw new SignedOutElsewhereError()
    }
    throw new Error(body?.error || res.statusText || "Kommunikasjon med server feilet")
  }

  throw new Error(res.statusText || "Kommunikasjon med server feilet")
}

// revokeSessionOnLogout best-effort revokes this device's single-active-session
// login server-side as the user logs out, so a flagged user's row never lingers
// to fire a false LOGIN_CONFLICT on their next sign-in. Fire-and-forget with
// keepalive:true so the request survives the logout redirect that navigates the
// page away immediately. (navigator.sendBeacon can't carry the Bearer token or
// x-login-id header this endpoint needs; a keepalive fetch can.) No-op for
// unflagged users, who never get a LoginId.
function revokeSessionOnLogout(): void {
  const loginId = getLoginId()
  if(!loginId || !idToken) return
  void fetch(apiUrl + "/session", {
    method: "DELETE",
    keepalive: true,
    headers: {
      authorization: `Bearer ${idToken}`,
      "x-login-id": loginId
    }
  }).catch(() => {
    // Best-effort: the row also ages out, and a stale row only ever affects
    // this same flagged user's own next login.
  })
}

// takeoverSession is the "Continue here" action: revoke the other device's
// login and mint one for this device. Persists the new LoginId so the next
// /auth-check passes.
export async function takeoverSession() {
  const res = await fetch(apiUrl + "/session/takeover", {
    method: "POST",
    headers: {
      authorization: `Bearer ${idToken}`
    }
  })

  if(res.status === 200) {
    const body = await res.json().catch(() => null)
    if(body?.loginId) setLoginId(body.loginId)
    return true
  }

  throw new Error("Kunne ikke overta økten")
}

export async function acquireAndCheckTokenOrRedirectToLogin() {
  try {
    await acquireTokenSilent()
    await checkTokenIsInSyncWithApi()
  } catch(error) {
    if(error instanceof InteractionRequiredAuthError) {
      return
    }

    // Single-active-session: flagged user signed in on another device.
    if(error instanceof LoginConflictError) {
      history.push(R.LOGIN_CONFLICT_PAGE.template, {
        liveLogin: error.liveLogin
      })
      return
    }

    // Single-active-session: this device was signed out elsewhere. Drop
    // the stale LoginId so the next sign-in mints a fresh one.
    if(error instanceof SignedOutElsewhereError) {
      clearLoginId()
      history.push(R.SIGNED_OUT_ELSEWHERE_PAGE.template)
      return
    }

    logError(error)

    const { pathname, search, hash } = window.location
    if(pathname === "/login") return

    let url = "/login"

    const returnUrl = `${pathname}${search}${hash}`
    if(returnUrl !== "/") {
      url += `?r=${encodeURIComponent(returnUrl)}`
    }

    history.push(url, { error })
  }
}

export async function onTokenExpired() {
  const clearWarning = window.addNotification({
    intent: "warning",
    timeout: false,
    iconSrc: "exclamation",
    loading: true,
    title: "Utløpt økt",
    content: "Vennligst vent mens vi autentiserer på nytt"
  })
  await acquireAndCheckTokenOrRedirectToLogin()
  clearWarning()
}
